import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  Image,
  VStack,
  HStack,
  Grid,
  Badge,
  SimpleGrid,
  Flex,
  Icon,
  Circle,
  useBreakpointValue,
  Link,
} from "@chakra-ui/react";
import {
  EmailIcon,
  ArrowRightIcon,
  ChatIcon,
  TimeIcon,
  ViewIcon,
  StarIcon,
  SettingsIcon,
  WarningTwoIcon,
  LinkIcon,
} from "@chakra-ui/icons";
import { FaBolt } from "react-icons/fa";
import { useEffect, createElement } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useUrlParams } from "../hooks/useUrlParams";
import { useShowToast } from "../hooks/useShowToast";
import { clearParams } from "../utils/clearParams";
import { CreateAccountModal } from "../components/modals/CreateAccountModal";
import { AccountTypeModal } from "../components/modals/AccountTypeModal";
import { SignInModal } from "../components/modals/SignInModal";
import { ForgotPasswordModal } from "../components/modals/ForgotPasswordModal";
import { useModal } from "../hooks/useModal";
import { PaymentMethodsMarquee } from "../components/PaymentMethodsMarquee";
import skipgiveLogo from "../assets/skipgive-logo.png";
import skipgiveIcon from "../assets/skipgive-icon.png";
import explainerPoster from "../assets/explainer-poster.png";
import explainerVideo from "../assets/skip-give-explainer.mp4";

const MotionBox = motion(Box);

export const Landing = () => {
  const { accessToken, stripeConnected, signOut } = useAuth();
  const { openModal } = useModal();
  const isMobile = useBreakpointValue({ base: true, lg: false });
  const navigate = useNavigate();
  const { queryParams } = useUrlParams();
  const { showToast } = useShowToast();
  const onboarding = localStorage.getItem("onboarding") === "true";

  // Premium color scheme matching the deck
  const primaryBlue = "#339CFF";
  const darkBlue = "#1a365d";
  const lightBlue = "#E3F2FD";
  const accentGreen = "#00C851";
  const warningOrange = "#FF6B35";
  const premiumGray = "#F8FAFC";
  const textDark = "#1A202C";

  useEffect(() => {
    if (queryParams.signOut === "true") {
      showToast("Signed out. Until next time 👋", "success");
      clearParams();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams.signOut]);

  useEffect(() => {
    if ((accessToken || stripeConnected) && !onboarding) {
      navigate("/dashboard");
    }
  }, [accessToken, navigate, onboarding, stripeConnected]);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <Box bg={premiumGray} overflow="hidden" maxW="100vw">
      <AccountTypeModal />
      <CreateAccountModal />
      <SignInModal />
      <ForgotPasswordModal />

      {/* Professional Header */}
      <Box
        position="fixed"
        top={0}
        width="100%"
        maxW="100vw"
        bg="rgba(255, 255, 255, 0.95)"
        backdropFilter="blur(10px)"
        zIndex={1000}
        py={{ base: 3, md: 4 }}
        px={{ base: 4, md: 8 }}
        boxShadow="0 2px 10px rgba(0,0,0,0.05)"
      >
        <Container maxW="1200px">
          <Flex align="center" justify="space-between">
            <Image
              src={skipgiveLogo}
              alt="SkipGive"
              height={{ base: "32px", md: "48px" }}
              cursor="pointer"
              onClick={() => window.location.reload()}
            />

            {!isMobile && (
              <HStack spacing={8}>
                <Button
                  variant="ghost"
                  color={textDark}
                  fontWeight="500"
                  onClick={() => scrollToSection("how-it-works")}
                  _hover={{ color: primaryBlue }}
                >
                  How It Works
                </Button>
                <Button
                  variant="ghost"
                  color={textDark}
                  fontWeight="500"
                  onClick={() => scrollToSection("pricing")}
                  _hover={{ color: primaryBlue }}
                >
                  Pricing
                </Button>
              </HStack>
            )}

            <HStack spacing={{ base: 2, md: 4 }}>
              {!accessToken ? (
                <>
                  {!isMobile && (
                    <Button
                      variant="ghost"
                      color={textDark}
                      fontWeight="500"
                      onClick={() => openModal("sign-in")}
                      _hover={{ color: primaryBlue }}
                    >
                      Sign In
                    </Button>
                  )}
                  <Button
                    size={{ base: "sm", md: "md" }}
                    bg={primaryBlue}
                    color="white"
                    px={{ base: 4, md: 6 }}
                    borderRadius="12px"
                    fontWeight="600"
                    _hover={{
                      bg: darkBlue,
                      transform: "translateY(-1px)",
                    }}
                    transition="all 0.3s"
                    onClick={() => openModal("choose-account-type")}
                  >
                    Get Started
                  </Button>
                </>
              ) : (
                <Button
                  size={{ base: "sm", md: "md" }}
                  variant="outline"
                  borderColor={primaryBlue}
                  color={primaryBlue}
                  onClick={signOut}
                  _hover={{
                    bg: primaryBlue,
                    color: "white",
                  }}
                >
                  Sign Out
                </Button>
              )}
            </HStack>
          </Flex>
        </Container>
      </Box>

      {/* Hero Section */}
      <MotionBox
        minH={{ base: "auto", md: "100vh" }}
        bgGradient={`linear(135deg, ${lightBlue} 0%, white 50%, ${premiumGray} 100%)`}
        display="flex"
        alignItems="center"
        justifyContent="center"
        position="relative"
        overflow="hidden"
        pt={{ base: "100px", md: 20 }}
        pb={{ base: 12, md: 0 }}
      >
        {/* Floating geometric shapes */}
        <Box position="absolute" top="10%" left="10%" opacity={0.08}>
          <Circle size="300px" bg={primaryBlue} />
        </Box>
        <Box position="absolute" bottom="15%" right="10%" opacity={0.05}>
          <Circle size="400px" bg={warningOrange} />
        </Box>

        <Container maxW="1000px" textAlign="center" zIndex={1}>
          <MotionBox
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
          >
            <Image
              src={skipgiveIcon}
              alt="SkipGive"
              height={isMobile ? "80px" : "120px"}
              mx="auto"
              mb={8}
              filter="drop-shadow(0 20px 40px rgba(51, 156, 255, 0.3))"
            />

            <Badge
              colorScheme="blue"
              variant="subtle"
              px={6}
              py={2}
              borderRadius="full"
              mb={6}
              fontSize="sm"
              fontWeight="600"
            >
              PROVEN CAMPAIGNS • REAL RESULTS
            </Badge>

            <Heading
              size={isMobile ? "xl" : "4xl"}
              color={textDark}
              fontWeight="800"
              lineHeight="1.1"
              mb={6}
              bgGradient={`linear(to-r, ${textDark}, ${darkBlue})`}
              bgClip="text"
            >
              Your Event Was Perfect.
              <br />
              <Text as="span" color={primaryBlue}>
                Your Reach Wasn't.
              </Text>
            </Heading>

            <Text
              fontSize={isMobile ? "lg" : "xl"}
              color="gray.600"
              maxW="600px"
              mx="auto"
              mb={6}
              fontWeight="500"
            >
              Most supporters can't attend your events - you're missing{" "}
              <Text as="span" color={warningOrange} fontWeight="700">
                90% of potential donations
              </Text>
              . We help you reach them with simple, effective campaigns that
              actually work.
            </Text>

            <VStack spacing={6}>
              <Button
                size={{ base: "md", md: "lg" }}
                bg={primaryBlue}
                color="white"
                px={{ base: 6, md: 8 }}
                py={{ base: 4, md: 6 }}
                borderRadius="12px"
                fontSize={{ base: "md", md: "lg" }}
                fontWeight="700"
                boxShadow="0 10px 30px rgba(51, 156, 255, 0.4)"
                _hover={{
                  bg: darkBlue,
                  transform: "translateY(-2px)",
                  boxShadow: "0 15px 40px rgba(51, 156, 255, 0.5)",
                }}
                transition="all 0.3s"
                rightIcon={<ArrowRightIcon />}
                onClick={() => openModal("choose-account-type")}
              >
                Start Your Campaign
              </Button>

              <Text fontSize="sm" color="gray.500">
                No setup fees • No monthly costs • Get started in minutes
              </Text>
            </VStack>
          </MotionBox>
        </Container>
      </MotionBox>

      {/* Payment Methods Marquee */}
      <PaymentMethodsMarquee />

      {/* Problem Section */}
      <Box bg={premiumGray} py={{ base: 12, md: 20 }}>
        <Container maxW="1200px">
          <Grid
            templateColumns={isMobile ? "1fr" : "1fr 1fr"}
            gap={{ base: 8, md: 16 }}
            alignItems="center"
          >
            <MotionBox
              initial={{ x: -80, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              viewport={{ once: true }}
            >
              <Badge colorScheme="red" mb={4} px={3} py={1} borderRadius="full">
                THE REALITY
              </Badge>

              <Heading
                size={{ base: "xl", md: "2xl" }}
                color={textDark}
                mb={6}
                fontWeight="800"
              >
                You're Missing Your
                <br />
                <Text as="span" color={warningOrange}>
                  Biggest Supporters
                </Text>
              </Heading>

              <VStack spacing={6} align="start">
                {[
                  {
                    icon: WarningTwoIcon,
                    title: "Most People Can't Attend",
                    desc: "Work, travel, health - life gets in the way",
                  },
                  {
                    icon: EmailIcon,
                    title: "Follow-up Emails Fail",
                    desc: "Generic blasts feel impersonal and get ignored",
                  },
                  {
                    icon: TimeIcon,
                    title: "Timing is Everything",
                    desc: "People give when they feel connected to the moment",
                  },
                ].map((item, index) => (
                  <MotionBox
                    key={index}
                    initial={{ y: 30, opacity: 0 }}
                    whileInView={{ y: 0, opacity: 1 }}
                    transition={{ delay: index * 0.2, duration: 0.6 }}
                    viewport={{ once: true }}
                  >
                    <HStack spacing={4} align="start">
                      <Circle size="50px" bg="red.50" color="red.500">
                        <Icon as={item.icon} boxSize={5} />
                      </Circle>
                      <Box>
                        <Text
                          fontWeight="700"
                          fontSize="lg"
                          color={textDark}
                          mb={1}
                        >
                          {item.title}
                        </Text>
                        <Text color="gray.600">{item.desc}</Text>
                      </Box>
                    </HStack>
                  </MotionBox>
                ))}
              </VStack>
            </MotionBox>

            <MotionBox
              initial={{ x: 80, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              viewport={{ once: true }}
            >
              <Box
                position="relative"
                height={{ base: "300px", md: "400px" }}
                bgGradient={`linear(135deg, ${warningOrange}, #FF8E53)`}
                borderRadius="24px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                color="white"
                overflow="hidden"
                boxShadow="0 20px 40px rgba(255, 107, 53, 0.3)"
              >
                <Box textAlign="center" zIndex={2}>
                  <Text
                    fontSize={{ base: "5xl", md: "6xl" }}
                    fontWeight="900"
                    mb={2}
                  >
                    90%
                  </Text>
                  <Text
                    fontSize={{ base: "lg", md: "xl" }}
                    fontWeight="600"
                    opacity={0.9}
                  >
                    MISSED OPPORTUNITIES
                  </Text>
                </Box>

                {/* Floating loss indicators */}
                <Box position="absolute" top="15%" left="15%">
                  <Icon as={WarningTwoIcon} fontSize="2xl" opacity={0.3} />
                </Box>
                <Box position="absolute" top="20%" right="20%">
                  <Icon
                    as={ArrowRightIcon}
                    fontSize="2xl"
                    opacity={0.3}
                    transform="rotate(45deg)"
                  />
                </Box>
                <Box position="absolute" bottom="25%" left="25%">
                  <Icon as={TimeIcon} fontSize="2xl" opacity={0.3} />
                </Box>
              </Box>
            </MotionBox>
          </Grid>
        </Container>
      </Box>

      {/* Solution Section */}
      <Box bg="white" py={{ base: 12, md: 20 }}>
        <Container maxW="1200px">
          <Grid
            templateColumns={isMobile ? "1fr" : "1fr 1fr"}
            gap={{ base: 8, md: 16 }}
            alignItems="center"
          >
            <MotionBox
              initial={{ x: -80, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              order={isMobile ? 2 : 1}
            >
              <Box
                height={{ base: "300px", md: "500px" }}
                bgGradient={`linear(135deg, ${primaryBlue}, ${darkBlue})`}
                borderRadius="24px"
                position="relative"
                overflow="hidden"
                boxShadow="0 25px 50px rgba(51, 156, 255, 0.3)"
              >
                <Box
                  position="absolute"
                  inset={0}
                  bgImage="url('https://walkwithsally.org/wp-content/uploads/2020/07/204411-IMG_9876-Kelton-Mattingly-1024x683.jpg')"
                  bgPosition="center"
                  bgSize="cover"
                  opacity={0.2}
                />

                <Flex
                  direction="column"
                  justify="center"
                  align="center"
                  height="100%"
                  position="relative"
                  zIndex={2}
                  color="white"
                  textAlign="center"
                  p={{ base: 6, md: 8 }}
                >
                  <Text
                    fontSize={{ base: "4xl", md: "5xl" }}
                    fontWeight="900"
                    mb={4}
                  >
                    3X
                  </Text>
                  <Text
                    fontSize={{ base: "lg", md: "xl" }}
                    fontWeight="600"
                    mb={6}
                  >
                    MORE ENGAGEMENT
                  </Text>
                  <Badge
                    colorScheme="green"
                    fontSize={{ base: "xs", md: "sm" }}
                    px={4}
                    py={2}
                  >
                    PROVEN IN REAL CAMPAIGNS
                  </Badge>
                </Flex>
              </Box>
            </MotionBox>

            <MotionBox
              initial={{ x: 80, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              order={isMobile ? 1 : 2}
            >
              <Badge
                colorScheme="green"
                mb={4}
                px={3}
                py={1}
                borderRadius="full"
              >
                THE SOLUTION
              </Badge>

              <Heading
                size={{ base: "xl", md: "2xl" }}
                color={textDark}
                mb={6}
                fontWeight="800"
              >
                Reach People Who Care
                <br />
                <Text as="span" color={accentGreen}>
                  When It Matters
                </Text>
              </Heading>

              <Text fontSize="lg" color="gray.600" mb={8}>
                We help you reach supporters who couldn't attend with
                personalized messages sent at the right moment. Simple,
                effective, authentic.
              </Text>

              <SimpleGrid columns={1} spacing={4}>
                {[
                  {
                    icon: ChatIcon,
                    title: "Personal Messages",
                    desc: "Heartfelt messages that feel genuine, not automated",
                  },
                  {
                    icon: TimeIcon,
                    title: "Right Timing",
                    desc: "Send when people are thinking about your event",
                  },
                  {
                    icon: LinkIcon,
                    title: "Easy Giving",
                    desc: "One click to donate - no complex forms or sign-ups",
                  },
                ].map((item, index) => (
                  <MotionBox
                    key={index}
                    initial={{ y: 30, opacity: 0 }}
                    whileInView={{ y: 0, opacity: 1 }}
                    transition={{ delay: index * 0.15, duration: 0.6 }}
                    viewport={{ once: true }}
                  >
                    <HStack
                      spacing={4}
                      bg="white"
                      p={6}
                      borderRadius="16px"
                      boxShadow="0 4px 20px rgba(0,0,0,0.05)"
                      _hover={{
                        transform: "translateY(-4px)",
                        boxShadow: "0 8px 30px rgba(0,0,0,0.1)",
                      }}
                      transition="all 0.3s"
                      border="1px solid"
                      borderColor="gray.100"
                    >
                      <Circle
                        size="50px"
                        bg={`${accentGreen}20`}
                        color={accentGreen}
                      >
                        <Icon as={item.icon} boxSize={5} />
                      </Circle>
                      <Box>
                        <Text
                          fontWeight="700"
                          fontSize="lg"
                          color={textDark}
                          mb={1}
                        >
                          {item.title}
                        </Text>
                        <Text color="gray.600" fontSize="sm">
                          {item.desc}
                        </Text>
                      </Box>
                    </HStack>
                  </MotionBox>
                ))}
              </SimpleGrid>
            </MotionBox>
          </Grid>
        </Container>
      </Box>

      {/* How It Works Section */}
      <Box id="how-it-works" bg={premiumGray} py={{ base: 12, md: 20 }}>
        <Container maxW="1200px">
          <MotionBox
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            textAlign="center"
            mb={{ base: 8, md: 16 }}
          >
            <Badge colorScheme="blue" mb={4} px={3} py={1} borderRadius="full">
              HOW IT WORKS
            </Badge>

            <Heading
              size={{ base: "xl", md: "2xl" }}
              color={textDark}
              mb={6}
              fontWeight="800"
            >
              Simple Process.
              <br />
              <Text as="span" color={primaryBlue}>
                Powerful Results.
              </Text>
            </Heading>

            <Text fontSize="lg" color="gray.600" maxW="600px" mx="auto">
              We help you reach supporters who couldn't attend with personalized
              messages sent at the right moment.
            </Text>
          </MotionBox>

          {/* Explainer Video */}
          <MotionBox
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            mt={{ base: 12, md: 16 }}
            mb={{ base: 12, md: 16 }}
          >
            <VStack spacing={6} maxW="900px" mx="auto">
              <Box
                width="100%"
                borderRadius="20px"
                overflow="hidden"
                boxShadow="0 20px 60px rgba(0, 0, 0, 0.15)"
                bg="black"
                position="relative"
                _hover={{
                  transform: "translateY(-4px)",
                  boxShadow: "0 25px 70px rgba(0, 0, 0, 0.2)",
                }}
                transition="all 0.3s"
              >
                <video
                  controls
                  style={{
                    width: "100%",
                    display: "block",
                  }}
                  poster={explainerPoster}
                >
                  <source src={explainerVideo} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </Box>
            </VStack>
          </MotionBox>

          {/* How It Works Header */}
          <MotionBox
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            textAlign="center"
            mb={{ base: 4, md: 6 }}
          >
            <Heading
              fontSize={{ base: "3xl", md: "4xl" }}
              fontWeight="800"
              color={textDark}
              mb={4}
            >
              How It Works
            </Heading>
            <Text fontSize="lg" color="gray.600" maxW="700px" mx="auto">
              Four simple steps to turn your events into fundraising success
            </Text>
          </MotionBox>

          <Grid
            templateColumns={{
              base: "1fr",
              md: "repeat(2, 1fr)",
              lg: "repeat(4, 1fr)",
            }}
            gap={{ base: 6, md: 8 }}
          >
            {[
              {
                title: "Share Your List",
                desc: "Upload your supporter contact list",
                icon: EmailIcon,
                color: primaryBlue,
              },
              {
                title: "We Create Messages",
                desc: "Personal, authentic messages for your cause",
                icon: ChatIcon,
                color: accentGreen,
              },
              {
                title: "Perfect Timing",
                desc: "Send when supporters are engaged",
                icon: TimeIcon,
                color: warningOrange,
              },
              {
                title: "Watch Results",
                desc: "Track donations and engagement",
                icon: ViewIcon,
                color: "#9333EA",
              },
            ].map((step, index) => (
              <MotionBox
                key={index}
                initial={{ y: 80, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ delay: index * 0.2, duration: 0.8 }}
                viewport={{ once: true }}
              >
                <VStack
                  spacing={6}
                  bg={premiumGray}
                  p={{ base: 6, md: 8 }}
                  borderRadius="20px"
                  height={{ base: "auto", md: "300px" }}
                  minH={{ base: "200px", md: "auto" }}
                  justify="center"
                  _hover={{
                    transform: "translateY(-8px)",
                    boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                    bg: "white",
                  }}
                  transition="all 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
                  position="relative"
                  overflow="hidden"
                >
                  <Circle size="70px" bg={`${step.color}20`} color={step.color}>
                    <Icon as={step.icon} boxSize={8} />
                  </Circle>

                  <Box textAlign="center">
                    <Text
                      fontWeight="800"
                      fontSize="lg"
                      color={textDark}
                      mb={2}
                    >
                      {step.title}
                    </Text>
                    <Text color="gray.600" fontSize="sm" lineHeight="short">
                      {step.desc}
                    </Text>
                  </Box>
                </VStack>
              </MotionBox>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* AI Campaign Builder Section */}
      <Box bg="white" py={{ base: 12, md: 20 }}>
        <Container maxW="1200px">
          <Grid
            templateColumns={isMobile ? "1fr" : "1fr 1fr"}
            gap={{ base: 8, md: 16 }}
            alignItems="center"
          >
            <MotionBox
              initial={{ x: -80, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <Badge
                colorScheme="purple"
                mb={4}
                px={3}
                py={1}
                borderRadius="full"
              >
                AI-POWERED BUILDER
              </Badge>

              <Heading
                size={{ base: "xl", md: "2xl" }}
                color={textDark}
                mb={6}
                fontWeight="800"
              >
                Build Campaigns in
                <br />
                <Text as="span" color="#9333EA">
                  Minutes, Not Hours
                </Text>
              </Heading>

              <Text fontSize="lg" color="gray.600" mb={8}>
                Our AI campaign builder understands your cause and creates
                personalized, authentic messages that resonate with your
                supporters. No marketing experience needed.
              </Text>

              <VStack spacing={4} align="start">
                {[
                  {
                    icon: StarIcon,
                    text: "Use AI to build your campaign in minutes",
                    color: "#FFD700",
                  },
                  {
                    icon: TimeIcon,
                    text: "Smart timing optimization for maximum engagement",
                    color: accentGreen,
                  },
                  {
                    icon: ViewIcon,
                    text: "Mobile-first design that makes donating easy",
                    color: primaryBlue,
                  },
                  {
                    icon: SettingsIcon,
                    text: "Real-time analytics and optimization",
                    color: "#9333EA",
                  },
                ].map((feature, index) => (
                  <MotionBox
                    key={index}
                    initial={{ x: -20, opacity: 0 }}
                    whileInView={{ x: 0, opacity: 1 }}
                    transition={{ delay: index * 0.1, duration: 0.6 }}
                    viewport={{ once: true }}
                  >
                    <HStack spacing={3} align="center">
                      <Icon
                        as={feature.icon}
                        color={feature.color}
                        boxSize={5}
                      />
                      <Text fontSize="md" fontWeight="500" color={textDark}>
                        {feature.text}
                      </Text>
                    </HStack>
                  </MotionBox>
                ))}
              </VStack>

              <Button
                mt={8}
                size={{ base: "md", md: "lg" }}
                bg="#9333EA"
                color="white"
                px={{ base: 6, md: 8 }}
                py={{ base: 4, md: 6 }}
                borderRadius="12px"
                fontSize={{ base: "md", md: "lg" }}
                fontWeight="700"
                boxShadow="0 10px 30px rgba(147, 51, 234, 0.4)"
                _hover={{
                  bg: "#7C3AED",
                  transform: "translateY(-2px)",
                  boxShadow: "0 15px 40px rgba(147, 51, 234, 0.5)",
                }}
                transition="all 0.3s"
                rightIcon={<ArrowRightIcon />}
                onClick={() => openModal("choose-account-type")}
              >
                Try AI Campaign Builder
              </Button>
            </MotionBox>

            <MotionBox
              initial={{ x: 80, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <Box
                position="relative"
                height={{ base: "400px", md: "500px" }}
                bgGradient="linear(135deg, #9333EA, #7C3AED, #8B5CF6)"
                borderRadius="24px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                color="white"
                overflow="hidden"
                boxShadow="0 25px 50px rgba(147, 51, 234, 0.3)"
                _hover={{
                  transform: "translateY(-4px)",
                  boxShadow: "0 35px 60px rgba(147, 51, 234, 0.4)",
                }}
                transition="all 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
              >
                {/* Animated floating orbs */}
                <MotionBox
                  position="absolute"
                  top="20%"
                  left="15%"
                  width="60px"
                  height="60px"
                  bgGradient="radial(circle, rgba(255,255,255,0.3), transparent)"
                  borderRadius="full"
                  animate={{
                    y: [0, -20, 0],
                    opacity: [0.3, 0.6, 0.3],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />
                <MotionBox
                  position="absolute"
                  bottom="25%"
                  right="20%"
                  width="40px"
                  height="40px"
                  bgGradient="radial(circle, rgba(255,255,255,0.2), transparent)"
                  borderRadius="full"
                  animate={{
                    y: [0, 15, 0],
                    opacity: [0.2, 0.5, 0.2],
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 1,
                  }}
                />
                <MotionBox
                  position="absolute"
                  top="60%"
                  left="70%"
                  width="30px"
                  height="30px"
                  bgGradient="radial(circle, rgba(255,255,255,0.25), transparent)"
                  borderRadius="full"
                  animate={{
                    y: [0, -10, 0],
                    opacity: [0.25, 0.4, 0.25],
                  }}
                  transition={{
                    duration: 5,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 2,
                  }}
                />

                {/* Enhanced AI-themed background pattern */}
                <Box position="absolute" inset={0} opacity={0.15}>
                  <svg
                    width="100%"
                    height="100%"
                    viewBox="0 0 400 400"
                    fill="none"
                  >
                    <defs>
                      <pattern
                        id="ai-grid"
                        width="40"
                        height="40"
                        patternUnits="userSpaceOnUse"
                      >
                        <circle
                          cx="20"
                          cy="20"
                          r="2"
                          fill="white"
                          opacity="0.4"
                        />
                        <path
                          d="M0 20h40M20 0v40"
                          stroke="white"
                          strokeWidth="0.5"
                          opacity="0.3"
                        />
                        <circle
                          cx="10"
                          cy="10"
                          r="1"
                          fill="white"
                          opacity="0.2"
                        />
                        <circle
                          cx="30"
                          cy="30"
                          r="1"
                          fill="white"
                          opacity="0.2"
                        />
                      </pattern>
                    </defs>
                    <rect width="100%" height="100%" fill="url(#ai-grid)" />
                  </svg>
                </Box>

                {/* Animated pulse ring */}
                <MotionBox
                  position="absolute"
                  width="200px"
                  height="200px"
                  border="2px solid"
                  borderColor="whiteAlpha.300"
                  borderRadius="full"
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0.3, 0.1, 0.3],
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />

                <VStack
                  spacing={6}
                  position="relative"
                  zIndex={2}
                  textAlign="center"
                >
                  <MotionBox
                    animate={{
                      rotate: [0, 360],
                    }}
                    transition={{
                      duration: 20,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                  >
                    <Circle
                      size="90px"
                      bg="whiteAlpha.200"
                      color="white"
                      border="2px solid"
                      borderColor="whiteAlpha.300"
                      backdropFilter="blur(10px)"
                      boxShadow="0 10px 30px rgba(0,0,0,0.2)"
                    >
                      <MotionBox
                        animate={{
                          rotate: [0, -360],
                        }}
                        transition={{
                          duration: 20,
                          repeat: Infinity,
                          ease: "linear",
                        }}
                      >
                        <Icon as={SettingsIcon} boxSize={12} />
                      </MotionBox>
                    </Circle>
                  </MotionBox>

                  <MotionBox
                    initial={{ scale: 0.8 }}
                    whileInView={{ scale: 1 }}
                    transition={{ delay: 0.3, duration: 0.6 }}
                  >
                    <Text
                      fontSize="2xl"
                      fontWeight="900"
                      mb={2}
                      textShadow="0 2px 10px rgba(0,0,0,0.3)"
                      bgGradient="linear(to-r, white, rgba(255,255,255,0.8))"
                      bgClip="text"
                    >
                      AI-POWERED
                    </Text>
                  </MotionBox>

                  <MotionBox
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ delay: 0.5, duration: 0.6 }}
                  >
                    <Text fontSize="lg" fontWeight="600" opacity={0.9} mb={4}>
                      Campaign Builder
                    </Text>
                  </MotionBox>

                  <MotionBox
                    initial={{ y: 20, opacity: 0 }}
                    whileInView={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.7, duration: 0.6 }}
                  >
                    <HStack
                      bg="rgba(0, 200, 81, 0.9)"
                      color="white"
                      fontSize="sm"
                      px={5}
                      py={2}
                      borderRadius="full"
                      fontWeight="700"
                      boxShadow="0 4px 15px rgba(0, 200, 81, 0.4)"
                      backdropFilter="blur(10px)"
                      border="1px solid"
                      borderColor="whiteAlpha.300"
                      spacing={2}
                    >
                      {createElement(FaBolt as any, {
                        size: 14,
                        color: "white",
                      })}
                      <Text>LAUNCHES IN MINUTES</Text>
                    </HStack>
                  </MotionBox>
                </VStack>
              </Box>
            </MotionBox>
          </Grid>
        </Container>
      </Box>

      {/* Results Preview */}
      <Box bg={textDark} color="white" py={{ base: 12, md: 20 }}>
        <Container maxW="1200px" textAlign="center">
          <MotionBox
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            mb={{ base: 8, md: 16 }}
          >
            <Badge
              bg="white"
              color={textDark}
              mb={6}
              px={4}
              py={2}
              borderRadius="full"
              fontWeight="700"
            >
              REAL RESULTS
            </Badge>

            <Heading size={{ base: "xl", md: "2xl" }} mb={6} fontWeight="800">
              What We've Learned
            </Heading>

            <Text fontSize="lg" opacity={0.8} maxW="600px" mx="auto">
              From campaigns we've run with nonprofits like yours
            </Text>
          </MotionBox>

          <SimpleGrid columns={isMobile ? 2 : 4} spacing={8}>
            {[
              {
                number: "3X",
                label: "Higher Response Rate",
                color: accentGreen,
              },
              {
                number: "72hrs",
                label: "Campaign Run Time",
                color: primaryBlue,
              },
              {
                number: "85%",
                label: "Give Within 24 Hours",
                color: warningOrange,
              },
              {
                number: "1 Min",
                label: "Average Donation Time",
                color: "#9333EA",
              },
            ].map((stat, index) => (
              <MotionBox
                key={index}
                initial={{ scale: 0.8, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                viewport={{ once: true }}
              >
                <VStack
                  spacing={3}
                  bg="whiteAlpha.100"
                  p={{ base: 6, md: 8 }}
                  borderRadius="20px"
                  backdropFilter="blur(10px)"
                  border="1px solid"
                  borderColor="whiteAlpha.200"
                  _hover={{
                    bg: "whiteAlpha.200",
                    transform: "translateY(-4px)",
                  }}
                  transition="all 0.3s"
                >
                  <Text
                    fontSize={{ base: "2xl", md: "3xl" }}
                    fontWeight="900"
                    bgGradient={`linear(to-r, ${stat.color}, white)`}
                    bgClip="text"
                  >
                    {stat.number}
                  </Text>
                  <Text
                    fontSize={{ base: "xs", md: "sm" }}
                    opacity={0.8}
                    fontWeight="600"
                  >
                    {stat.label}
                  </Text>
                </VStack>
              </MotionBox>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* Pricing Section */}
      <Box
        id="pricing"
        bg={primaryBlue}
        color="white"
        py={{ base: 12, md: 20 }}
      >
        <Container maxW="800px" textAlign="center">
          <MotionBox
            initial={{ scale: 0.9, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <Badge
              bg="white"
              color={primaryBlue}
              mb={6}
              px={4}
              py={2}
              borderRadius="full"
              fontWeight="700"
            >
              TRANSPARENT PRICING
            </Badge>

            <Heading size={{ base: "xl", md: "2xl" }} mb={8} fontWeight="800">
              Success-Based Pricing
            </Heading>

            <Box
              bg="whiteAlpha.100"
              p={{ base: 8, md: 12 }}
              borderRadius="24px"
              backdropFilter="blur(10px)"
              border="2px solid"
              borderColor="whiteAlpha.300"
              mb={8}
            >
              <Text
                fontSize={{ base: "4xl", md: "6xl" }}
                fontWeight="900"
                mb={4}
              >
                FREE
              </Text>
              <Text fontSize={{ base: "lg", md: "xl" }} mb={6} opacity={0.9}>
                When donors add a tip for SkipGive
              </Text>
              <Text fontSize={{ base: "md", md: "lg" }} opacity={0.8}>
                Otherwise, just 1% per transaction
              </Text>
            </Box>

            <Text fontSize="lg" opacity={0.9} mb={8}>
              No setup fees • No monthly costs • No hidden charges
            </Text>

            <Button
              size={{ base: "md", md: "lg" }}
              bg="white"
              color={primaryBlue}
              px={{ base: 6, md: 8 }}
              py={{ base: 4, md: 6 }}
              borderRadius="12px"
              fontSize={{ base: "md", md: "lg" }}
              fontWeight="700"
              _hover={{
                transform: "translateY(-2px)",
                boxShadow: "0 8px 30px rgba(0,0,0,0.2)",
              }}
              transition="all 0.3s"
              onClick={() => openModal("choose-account-type")}
            >
              Get Started Today
            </Button>
          </MotionBox>
        </Container>
      </Box>

      {/* Professional Footer */}
      <Box bg={textDark} color="white" py={{ base: 12, md: 16 }}>
        <Container maxW="1200px">
          <Grid
            templateColumns={isMobile ? "1fr" : "repeat(4, 1fr)"}
            gap={8}
            mb={{ base: 8, md: 12 }}
          >
            <VStack align="start" spacing={4}>
              <Image
                src={skipgiveIcon}
                alt="SkipGive"
                height="40px"
                filter="brightness(0) invert(1)"
              />
              <Text color="gray.400" fontSize="sm" maxW="250px">
                Helping nonprofits reach supporters who couldn't attend their
                events with simple, effective campaigns.
              </Text>
            </VStack>

            <VStack align="start" spacing={3}>
              <Text fontWeight="600" mb={2}>
                Product
              </Text>
              <Link
                color="gray.400"
                fontSize="sm"
                _hover={{ color: "white" }}
                onClick={() => scrollToSection("how-it-works")}
              >
                How It Works
              </Link>
              <Link
                color="gray.400"
                fontSize="sm"
                _hover={{ color: "white" }}
                onClick={() => scrollToSection("pricing")}
              >
                Pricing
              </Link>
            </VStack>

            <VStack align="start" spacing={3}>
              <Text fontWeight="600" mb={2}>
                Company
              </Text>
              <Link
                color="gray.400"
                fontSize="sm"
                _hover={{ color: "white" }}
                href="mailto:hello@skipgive.com"
              >
                Contact Us
              </Link>
              <Link
                color="gray.400"
                fontSize="sm"
                _hover={{ color: "white" }}
                onClick={() => navigate("/tos")}
              >
                Terms of Service
              </Link>
              <Link
                color="gray.400"
                fontSize="sm"
                _hover={{ color: "white" }}
                onClick={() => navigate("/sms-policy")}
              >
                SMS Policy
              </Link>
            </VStack>

            <VStack align="start" spacing={3}>
              <Text fontWeight="600" mb={2}>
                Get Started
              </Text>
              <Link
                color="gray.400"
                fontSize="sm"
                _hover={{ color: "white" }}
                onClick={() => openModal("choose-account-type")}
              >
                Create Account
              </Link>
              <Link
                color="gray.400"
                fontSize="sm"
                _hover={{ color: "white" }}
                onClick={() => openModal("sign-in")}
              >
                Sign In
              </Link>
              <Text color="gray.400" fontSize="sm">
                <Icon as={EmailIcon} mr={2} />
                hello@skipgive.com
              </Text>
            </VStack>
          </Grid>

          <Box
            borderTop="1px solid"
            borderColor="gray.700"
            pt={8}
            textAlign="center"
          >
            <Text color="gray.400" fontSize="sm">
              © 2024 SkipGive. All rights reserved. • Helping nonprofits reach
              every supporter.
            </Text>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};
